import React from 'react';
import PropTypes from 'prop-types';
import { Collapsible, Container } from '@edx/paragon';
import { injectIntl } from 'gatsby-plugin-react-intl';

import { mobilePageHeaders } from '@utils/course';
import { intlShape } from '@prospectus/common/types/intlShape';
import { isEmptyHtml } from '@utils/formatting';
import courseRunShape from '@prospectus/common/types/courseRunShape';
import courseShape from '@prospectus/common/types/courseShape';
import activeProgramShape from '@prospectus/common/types/activeProgramShape';
import ProgramUpsell from '@prospectus/open-course-page/components/ProgramUpsell';

import useBreakpointMatch from '@prospectus/common/hooks/useBreakpointMatch';
import CourseHeader from './CourseHeader';
import CourseRunSelector from './CourseRunSelector';
import CourseMain from './CourseMain/variants/MobileVariant';
import Instructors from './Instructors/variants/MobileVariant';
import FAQ from './FAQ';
import CourseModes from './CourseModes';
import SocialShare from './SocialShare';
import BusinessBlock from './BusinessBlock/variants/MobileVariant';
import Outcomes from './CourseOutcomes';
import CourseRecommendations from './CourseRecommendations';

function CoursePageMobile({
  courseRun,
  course,
  activeProgram,
  activeCourseRun,
  refMap,
  showCourseModes,
  emailOptIn,
  setEmailOptIn,
  sortedPrograms,
  sharedMessages,
  paymentOption,
  webview,
  userLoggedIn,
  isEnterpriseUser,
  intl,
}) {
  const isMobile = useBreakpointMatch('medium');
  // Hardcoded setting the flag to false so that the recommendations are not shown.
  // This will be used by student engagement team for Cross Product Recommendation.
  const showAlgoliaRecommendations = !webview && isMobile !== undefined && false;
  const showProgramUpsell = !webview && sortedPrograms.length !== 0 && (!userLoggedIn || isEnterpriseUser === false);

  return (
    <div className="course-about mobile course-info-content" data-course-run-key={courseRun.key}>
      {/* 'key' has to go after '...course' in props to avoid the react key getting
      overriden by the course key */}

      <CourseHeader {...course} key={`${course.key}-header`} activeProgram={activeProgram} mobile />

      <CourseRunSelector
        {...course}
        key={`${courseRun.key}-selection`}
        activeCourseRuns={course.activeCourseRuns}
        refProp={refMap.enrollRef}
        emailOptIn={emailOptIn}
        setEmailOptIn={setEmailOptIn}
        paymentOption={paymentOption}
        mobile
      />

      <Collapsible title={intl.formatMessage(mobilePageHeaders['prospectus.course.detail.mobile.main.about'])}>
        <CourseMain
          refMapProp={{
            aboutRef: refMap.aboutRef,
            learnRef: refMap.learnRef,
            syllabusRef: refMap.syllabusRef,
          }}
          key={`${courseRun.key}-main`}
          course={course}
          programs={sortedPrograms}
          paymentOption={paymentOption}
        />
      </Collapsible>
      {course.outcome && (
        <Collapsible title={intl.formatMessage(mobilePageHeaders['prospectus.course.detail.mobile.main.outcomes'])}>
          <Container size="sm">
            <Outcomes {...course} />
          </Container>
        </Collapsible>
      )}
      <Collapsible title={intl.formatMessage(mobilePageHeaders['prospectus.course.detail.mobile.main.instructors'])}>
        <Instructors
          refProp={refMap.instructorsRef}
          key={`${courseRun.key}-instructors`}
          staff={activeCourseRun.staff}
        />
        <ProgramUpsell programs={sortedPrograms} showProgramUpsell={showProgramUpsell} courseKey={courseRun.key} />
      </Collapsible>
      {/* Only show collapsible if there is FAQ content */}
      {((course.faq && !isEmptyHtml(course.faq))
        || (course.additionalInformation && !isEmptyHtml(course.additionalInformation))
        || course.hasOfacRestrictions) && (
        <Collapsible title={intl.formatMessage(mobilePageHeaders['prospectus.course.detail.mobile.main.faq'])}>
          <FAQ
            refProp={refMap.faqsRef}
            commonMessages={sharedMessages}
            {...course}
            key={`${courseRun.key}-faq`}
            mobile
          />
        </Collapsible>
      )}
      {/* Only show collapsible if course has different modes */}
      {showCourseModes && (
        <Collapsible
          id="course-modes"
          title={intl.formatMessage(mobilePageHeaders['prospectus.course.detail.mobile.main.course-modes'])}
        >
          <CourseModes
            {...course}
            refProp={refMap.waysRef}
            key={`${courseRun.key}-modes`}
            emailOptIn={emailOptIn}
            showCourseModes={showCourseModes}
            showProgramUpsell={showProgramUpsell}
            mobile
          />
        </Collapsible>
      )}
      {activeCourseRun.marketingUrl && (
        <Collapsible title={intl.formatMessage(mobilePageHeaders['prospectus.course.detail.mobile.main.share'])}>
          <SocialShare {...course} key={`${courseRun.key}-social`} mobile />
        </Collapsible>
      )}
      {!webview && (
        <Collapsible title={intl.formatMessage(mobilePageHeaders['prospectus.course.detail.mobile.main.business'])}>
          <BusinessBlock {...course} key={`${courseRun.key}-business`} />
        </Collapsible>
      )}
      {showAlgoliaRecommendations && (
        <CourseRecommendations course={course} shouldFetchRecommendations={isMobile === true} />
      )}
    </div>
  );
}

CoursePageMobile.propTypes = {
  courseRun: PropTypes.shape(courseRunShape).isRequired,
  course: PropTypes.shape(courseShape).isRequired,
  activeProgram: PropTypes.shape(activeProgramShape),
  activeCourseRun: PropTypes.shape(courseRunShape).isRequired,
  refMap: PropTypes.shape().isRequired,
  showCourseModes: PropTypes.bool.isRequired,
  emailOptIn: PropTypes.bool.isRequired,
  setEmailOptIn: PropTypes.func.isRequired,
  sortedPrograms: PropTypes.arrayOf(PropTypes.shape(activeProgramShape)).isRequired,
  sharedMessages: PropTypes.shape().isRequired,
  paymentOption: PropTypes.string.isRequired,
  webview: PropTypes.bool,
  userLoggedIn: PropTypes.bool,
  isEnterpriseUser: PropTypes.bool,
  intl: intlShape.isRequired,
};

CoursePageMobile.defaultProps = {
  activeProgram: null,
  webview: false,
  userLoggedIn: false,
  isEnterpriseUser: false,
};

export default injectIntl(CoursePageMobile);
